export interface ExampleStation {
    name: string;
    description: string;
}

export interface PreviewStop {
    id: string;
    name: string;
    type: string;
    status: string;
    asynchronousChildren: boolean;
}

// Recursive type for track structure
type TrackNode = { [id: string]: TrackNode[] };

export interface ExampleWorkflow {
    id: string;
    name: string;
    description: string;
    stations: ExampleStation[];
    previewTrack: TrackNode;
    previewStops: PreviewStop[];
}

export const EXAMPLE_WORKFLOWS: Record<string, ExampleWorkflow> = {
    production: {
        id: 'production',
        name: 'Production Workflow - Order 2813',
        description: 'A typical production workflow with parallel assembly lines',
        stations: [
            { name: 'Receiving', description: 'Incoming materials inspection' },
            { name: 'Quality Check', description: 'Initial quality verification' },
            { name: 'Assembly Line A', description: 'Primary assembly station' },
            { name: 'Assembly Line B', description: 'Secondary assembly station' },
            { name: 'Packaging', description: 'Final packaging station' },
            { name: 'Shipping', description: 'Outbound shipping' }
        ],
        // Track structure: Receiving → Quality Check → [Assembly A → Packaging → Shipping, Assembly B → Packaging → Shipping] (parallel)
        previewTrack: {
            receiving: [
                {
                    'quality-check': [
                        {
                            'assembly-a': [{ packaging: [{ shipping: [] }] }]
                        },
                        {
                            'assembly-b': [{ 'packaging-b': [{ 'shipping-b': [] }] }]
                        }
                    ]
                }
            ]
        },
        previewStops: [
            { id: 'receiving', name: 'Receiving', type: 'station', status: '', asynchronousChildren: false },
            {
                id: 'quality-check',
                name: 'Quality Check',
                type: 'station',
                status: '',
                asynchronousChildren: true
            },
            { id: 'assembly-a', name: 'Assembly Line A', type: 'station', status: '', asynchronousChildren: false },
            { id: 'assembly-b', name: 'Assembly Line B', type: 'station', status: '', asynchronousChildren: false },
            { id: 'packaging', name: 'Packaging', type: 'station', status: '', asynchronousChildren: false },
            { id: 'shipping', name: 'Shipping', type: 'station', status: '', asynchronousChildren: false },
            { id: 'packaging-b', name: 'Packaging', type: 'station', status: '', asynchronousChildren: false },
            { id: 'shipping-b', name: 'Shipping', type: 'station', status: '', asynchronousChildren: false }
        ]
    },
    realEstate: {
        id: 'realEstate',
        name: 'Real Estate Flipping',
        description: 'Purchasing and renovating property for resale',
        stations: [
            { name: 'Flip Property', description: 'Finding investment properties' },
            { name: 'Due Diligence', description: 'Property inspection and research' },
            { name: 'Purchase/Closing', description: 'Complete property purchase' },
            { name: 'Renovation Planning', description: 'Plan renovation scope' },
            { name: 'Contractor Work', description: 'Execute renovations' },
            { name: 'Staging', description: 'Prepare property for sale' },
            { name: 'Listing', description: 'List property on market' },
            { name: 'Sale/Closing', description: 'Complete property sale' }
        ],
        // Track structure: Flip Property → [Due Diligence → Purchase → [Renovation → Contractor, Staging → Listing], Sale/Closing]
        previewTrack: {
            flip: [
                {
                    diligence: [
                        {
                            purchase: [
                                {
                                    planning: [{ contractor: [] }]
                                },
                                {
                                    staging: [{ listing: [] }]
                                }
                            ]
                        }
                    ]
                },
                {
                    sale: []
                }
            ]
        },
        previewStops: [
            { id: 'flip', name: 'Flip Property', type: 'station', status: '', asynchronousChildren: false },
            { id: 'diligence', name: 'Due Diligence', type: 'station', status: '', asynchronousChildren: false },
            { id: 'purchase', name: 'Purchase/Closing', type: 'station', status: '', asynchronousChildren: false },
            { id: 'planning', name: 'Renovation Planning', type: 'station', status: '', asynchronousChildren: false },
            { id: 'contractor', name: 'Contractor Work', type: 'station', status: '', asynchronousChildren: false },
            { id: 'staging', name: 'Staging', type: 'station', status: '', asynchronousChildren: false },
            { id: 'listing', name: 'Listing', type: 'station', status: '', asynchronousChildren: false },
            { id: 'sale', name: 'Sale/Closing', type: 'station', status: '', asynchronousChildren: false }
        ]
    },
    onlineRetail: {
        id: 'onlineRetail',
        name: 'Online Retail Product',
        description: 'Creating and fulfilling an online retail product',
        stations: [
            { name: 'New Product 288', description: 'New product entry' },
            { name: 'Product Design', description: 'Design product specifications' },
            { name: 'Sample Review', description: 'Review product samples' },
            { name: 'Inventory Order', description: 'Order inventory stock' },
            { name: 'Photography', description: 'Product photography' },
            { name: 'Listing Creation', description: 'Create online listing' },
            { name: 'Order Received', description: 'Process customer orders' },
            { name: 'Fulfillment', description: 'Pick and pack orders' }
        ],
        // Track structure: New Product 288 → [Product Design → Inventory → [Order Received, Photography, Listing], Sample Review, Fulfillment]
        previewTrack: {
            'new-product': [
                {
                    'product-design': [
                        {
                            inventory: [{ 'order-received': [] }, { photography: [] }, { listing: [] }]
                        }
                    ]
                },
                { sample: [] },
                { fulfillment: [] }
            ]
        },
        previewStops: [
            { id: 'new-product', name: 'New Product 288', type: 'station', status: '', asynchronousChildren: false },
            { id: 'product-design', name: 'Product Design', type: 'station', status: '', asynchronousChildren: false },
            { id: 'sample', name: 'Sample Review', type: 'station', status: '', asynchronousChildren: false },
            { id: 'inventory', name: 'Inventory Order', type: 'station', status: '', asynchronousChildren: false },
            { id: 'photography', name: 'Photography', type: 'station', status: '', asynchronousChildren: false },
            { id: 'listing', name: 'Listing Creation', type: 'station', status: '', asynchronousChildren: false },
            {
                id: 'order-received',
                name: 'Order Received',
                type: 'station',
                status: '',
                asynchronousChildren: false
            },
            { id: 'fulfillment', name: 'Fulfillment', type: 'station', status: '', asynchronousChildren: false }
        ]
    }
};
